import { useEffect, useRef, useState } from 'react';
import { useMachine } from '../../state/machine.ts';
import { useSession } from '../../state/session.store.ts';
import { useNetworkClass } from '../../hooks/useNetworkClass.ts';
import { usePoW } from '../../hooks/usePoW.ts';
import { startScorer } from '../../services/scorerClient.ts';
import { WarmupBar, type WarmupTask } from './WarmupBar.tsx';
import s from './Calibration.module.css';

export function Calibration() {
  const send = useMachine((m) => m.send);
  const session = useSession();
  const network = useNetworkClass();
  const pow = usePoW();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [iceTask, setIceTask] = useState<WarmupTask['status']>('pending');
  const [modelTask, setModelTask] = useState<WarmupTask['status']>('pending');

  // Wire camera (already granted by Permission step)
  useEffect(() => {
    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
      } catch {
        send('CAMERA_DENIED');
      }
    })();
  }, [send]);

  // Dummy ICE warmup (caches host candidates so the real PC negotiates faster).
  useEffect(() => {
    setIceTask('running');
    const pc = new RTCPeerConnection();
    pc.createDataChannel('warmup');
    pc.createOffer({ offerToReceiveVideo: true })
      .then((offer) => pc.setLocalDescription(offer))
      .then(() => {
        setIceTask('done');
        pc.close();
      })
      .catch(() => {
        setIceTask('error');
        pc.close();
      });
    return () => pc.close();
  }, []);

  // Real MediaPipe FaceLandmarker warmup — initialises the worker so the duel
  // doesn't pay the load cost mid-match.
  useEffect(() => {
    setModelTask('running');
    let destroyed = false;
    const handle = startScorer({ network });
    handle.ready
      .then(() => {
        if (!destroyed) setModelTask('done');
      })
      .catch((err) => {
        console.error('[calibration] model load failed', err);
        if (!destroyed) setModelTask('error');
      });
    return () => {
      destroyed = true;
      handle.destroy();
    };
  }, [network]);

  // PoW (deferred until consent recorded — should already be true by now)
  useEffect(() => {
    if (session.token) return; // already have session
    void pow.run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tasks: WarmupTask[] = [
    {
      id: 'ice',
      label: 'caching network',
      status: iceTask,
      ...(iceTask === 'error' ? { detail: 'failed' } : {}),
    },
    {
      id: 'model',
      label: network === 'slow' ? 'loading model (lite)' : 'loading model',
      status: modelTask,
    },
    {
      id: 'pow',
      label: 'minting session',
      status: session.token
        ? 'done'
        : pow.status === 'idle'
          ? 'pending'
          : pow.status === 'done'
            ? 'done'
            : pow.status === 'error'
              ? 'error'
              : 'running',
      ...(pow.status === 'solving' && pow.hashesPerSec > 0
        ? { detail: `${(pow.hashesPerSec / 1000).toFixed(0)}k h/s` }
        : {}),
    },
  ];

  const allDone = tasks.every((t) => t.status === 'done');

  useEffect(() => {
    if (allDone) {
      const t = setTimeout(() => send('WARMUP_DONE'), 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [allDone, send]);

  return (
    <main className={s.root}>
      <div className={s.frame}>
        <video ref={videoRef} className={s.video} autoPlay playsInline muted />
        <div className={s.scan} aria-hidden />
      </div>
      <h2 className={s.title}>warming up arena</h2>
      <WarmupBar tasks={tasks} />
      {pow.status === 'error' ? (
        <p className={s.error}>session error: {pow.error}</p>
      ) : null}
      <button type="button" className={s.cancel} onClick={() => send('BACK_TO_LANDING')}>
        cancel
      </button>
    </main>
  );
}
